import type { TFunction } from "i18next";
import { useState } from "react";
import type { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";
import { emptyInvoiceLine, type U3125InvoiceFormRow, type U3125InvoiceLineGridRow } from "../../../../U3125Service";

export interface U3125FactureNestedParentHandlers {
  getData: () => U3125InvoiceFormRow | null;
  setData: (data: U3125InvoiceFormRow) => void;
  getFormState?: () => U2000FormStateEnumState;
}

export interface U3125InvoiceLineHandlerReturn {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onRowClick: (row: U3125InvoiceLineGridRow) => void;
  onDlgClose: (result: string) => void;
  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3125InvoiceLineGridRow>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3125InvoiceLineGridRow>>;
  dataChangedEvent: number;
}

export interface U3125InvoiceLineHandlerParam {
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3125InvoiceLineGridRow>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3125InvoiceLineGridRow>>;
  parentFormHandlers: U3125FactureNestedParentHandlers;
}

export function useU3125InvoiceLineHandler(params: U3125InvoiceLineHandlerParam): U3125InvoiceLineHandlerReturn {
  const { gridStateHandlers, formStateHandlers, parentFormHandlers } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);

  const onAdd = (): void => {
    const inv = parentFormHandlers.getData();
    const n = inv?.lines?.length ?? 0;
    formStateHandlers.setData({ ...emptyInvoiceLine(n), id: "" } as U3125InvoiceLineGridRow);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    setDialogOpen(true);
  };

  const onModify = (): void => {
    const selectedRow = gridStateHandlers.getSelectedRows()[0] as U3125InvoiceLineGridRow | undefined;
    if (!selectedRow) return;
    formStateHandlers.setData({ ...selectedRow });
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    setDialogOpen(true);
  };

  const onDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  const onRowClick = (row: U3125InvoiceLineGridRow): void => {
    formStateHandlers.setData({ ...row });
    const parentFormState = parentFormHandlers.getFormState?.();
    if (parentFormState === U2000FormStateEnumState.EDIT) {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    } else {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.NONE);
    }
    setDialogOpen(true);
  };

  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
    } else if (result === "save") {
      const businessHandlers = formStateHandlers.getBusinessHandlers();
      if (businessHandlers?.validateForm) {
        const errors = businessHandlers.validateForm(formStateHandlers.getData());
        formStateHandlers.setErrors(errors);
        formStateHandlers.saveData();
        if (Object.keys(errors).length > 0) return;
      }
      if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT) {
        const saved = formStateHandlers.getData();
        const updatedData = gridStateHandlers.getData().map((item) => (item.id === saved.id ? { ...saved } : item));
        gridStateHandlers.setData(updatedData);
        setDataChangedEvent((prev) => prev + 1);
      } else if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD) {
        const newData = { ...formStateHandlers.getData() } as U3125InvoiceLineGridRow;
        newData.id = generateTemporaryId();
        gridStateHandlers.setData([...gridStateHandlers.getData(), newData]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        setDataChangedEvent((prev) => prev + 1);
      }
      formStateHandlers.afterSaveData(formStateHandlers.getData());
    }
    setDialogOpen(false);
  };

  const onDlgDeleteClose = (result: string): void => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((selected) => selected.id === row.id));
      gridStateHandlers.setData(remainingRows);
      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent((prev) => prev + 1);
    }
    setDeleteDialogOpen(false);
  };

  return {
    onAdd,
    onModify,
    onDelete,
    onRowClick,
    onDlgClose,
    isDialogOpen,
    setDialogOpen,
    formStateHandlers,
    gridStateHandlers,
    isDeleteDialogOpen,
    onDlgDeleteClose,
    dataChangedEvent,
  };
}
